import { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import DensityMediumOutlined from '@mui/icons-material/DensityMediumOutlined';
import DensitySmallOutlined from '@mui/icons-material/DensitySmallOutlined';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import MenuOutlined from '@mui/icons-material/MenuOutlined';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Outlet } from 'react-router-dom';

import { ConfirmacaoHumana } from '../ui/ConfirmacaoHumana';
import { BarraLateral, LARGURA_DA_BARRA } from './BarraLateral';
import { usePreferencias } from '../preferencias/usePreferencias';
import { useIdentidadeDoShell } from './useIdentidadeDoShell';

export function Shell(): React.JSX.Element {
  const aberta = usePreferencias((estado) => estado.barraLateralAberta);
  const alternarBarraLateral = usePreferencias((estado) => estado.alternarBarraLateral);
  const densidade = usePreferencias((estado) => estado.densidade);
  const alternarDensidade = usePreferencias((estado) => estado.alternarDensidade);

  const { nome, sair } = useIdentidadeDoShell();
  // Sair pede confirmacao: o icone fica colado no de densidade, e um clique
  // errado derrubaria a sessao — e entrar de novo custa um codigo por e-mail.
  const [confirmandoSaida, definirConfirmandoSaida] = useState(false);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={(tema) => ({
          zIndex: tema.zIndex.drawer + 1,
          backgroundColor: tema.palette.primary.main,
          color: tema.palette.primary.contrastText,
        })}
      >
        <Toolbar variant="dense">
          <IconButton edge="start" color="inherit" onClick={alternarBarraLateral} aria-label="Alternar menu">
            <MenuOutlined />
          </IconButton>

          <Typography variant="h3" component="h1" sx={{ ml: 1, flexGrow: 1 }}>
            Plataforma Anery
          </Typography>

          <Tooltip title={densidade === 'compacta' ? 'Densidade compacta (32px)' : 'Densidade confortável (40px)'}>
            <IconButton color="inherit" onClick={alternarDensidade} aria-label="Alternar densidade">
              {densidade === 'compacta' ? <DensitySmallOutlined /> : <DensityMediumOutlined />}
            </IconButton>
          </Tooltip>

          <Typography variant="body2" sx={{ mx: 1, display: { xs: 'none', sm: 'block' } }}>
            {nome}
          </Typography>

          <Tooltip title="Sair">
            <IconButton
              color="inherit"
              onClick={() => {
                definirConfirmandoSaida(true);
              }}
              aria-label="Sair"
            >
              <LogoutOutlined />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <ConfirmacaoHumana
        aberto={confirmandoSaida}
        titulo="Sair da plataforma?"
        descricao="Sua sessão será encerrada. Deseja prosseguir (clique em SAIR)?"
        rotuloDeConfirmar="Sair"
        aoConfirmar={() => {
          definirConfirmandoSaida(false);
          sair();
        }}
        aoCancelar={() => {
          definirConfirmandoSaida(false);
        }}
      />

      <Drawer
        variant="persistent"
        open={aberta}
        sx={{
          width: aberta ? LARGURA_DA_BARRA : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: LARGURA_DA_BARRA, boxSizing: 'border-box' },
        }}
      >
        <Toolbar variant="dense" />
        <BarraLateral />
      </Drawer>

      {/* Respiro lateral maior que o vertical: `espacoEntreBlocos` é a
          distância ENTRE blocos, e usá-lo também como margem da página
          deixava o texto a 16px da barra lateral — em 1600px de largura,
          isso lê como se o conteúdo tivesse escorregado para fora. 48px é o
          menor valor em que a coluna da esquerda para de parecer colada. */}
      <Box
        component="main"
        sx={(tema) => ({
          flexGrow: 1,
          minWidth: 0,
          py: tema.anery.espacoEntreBlocos,
          px: tema.anery.espacoEntreBlocos * 3,
        })}
      >
        <Toolbar variant="dense" />
        <Outlet />
      </Box>
    </Box>
  );
}
